/**
 * Staff-only payment policy for EditParticipant.
 * These rules take priority over member-site helpers in invoiceCalculations /
 * registrationPaymentStatus for this screen.
 */

import {
  paymentMethodEnum,
  statusEnum,
  type AttendeeType,
  type PaymentMethod,
  type StatusType,
} from "../../../../Enums/RegistrationEnum";
import { IEvent } from "../../../../interfaces/models/IEvent";
import { IRegistration, IRegistrationGuest } from "../../../../interfaces/models/IRegistration";
import {
  getSpecialEventsTotalAmount,
  parseAdditionalFee,
  shouldUseLateFeesForRegistration,
} from "../../../../utils/invoiceCalculations";
import { isStaffNoPayAttendee } from "../../../../utils/staffRegistrationRules";
import { normalizeRegistrationGuests } from "../../../../utils/registrationGuests";

export type StaffFormPaymentState = {
  guests?: IRegistration["guests"];
  selectedSpecialEventIds?: string[];
  specialEventGuestsMap?: Record<string, IRegistrationGuest[]>;
  additionalFeeInput?: string;
  /** True when the payment ledger already contains a prepaid row. */
  hasPrepaidPaymentInLedger?: boolean;
};

const BILLABLE_TYPES: AttendeeType[] = ["regular", "regularSpeaker"];
const NO_CHARGE_TYPES: AttendeeType[] = [
  "sponsor",
  "speaker",
  "invited_guest",
  "staff",
];

const BILLABLE_PAYMENT_METHODS: PaymentMethod[] = [
  "creditCard",
  "wireTransfer",
  "check",
];
const NEW_PAYMENT_METHODS: PaymentMethod[] = [
  "creditCard",
  "wireTransfer",
  "check",
  "prepaid",
];

const roundAmount = (n: number): number => Math.round(n * 100) / 100;

/** Parses the incremental staff payment amount from New Payment (not cumulative amountPaid). */
export function parsePaymentIncrement(input: string | undefined): number | null {
  if (input == null || input.trim() === "") {
    return null;
  }
  const parsed = parseFloat(input);
  if (Number.isNaN(parsed) || parsed === 0 || parsed < 0) {
    return null;
  }
  return roundAmount(parsed);
}

export function isBillableMemberType(
  type?: AttendeeType | string,
): boolean {
  return BILLABLE_TYPES.includes(type as AttendeeType);
}

export function isNoChargeMemberType(
  type?: AttendeeType | string,
): boolean {
  return NO_CHARGE_TYPES.includes(type as AttendeeType);
}

export function getDefaultNoChargeStatus(
  type?: AttendeeType | string,
): StatusType {
  if (type === "sponsor") {
    return "paidSPR";
  }
  if (type === "speaker") {
    return "paidSPK";
  }
  return "completed";
}

const MAIN_EVENT_GUEST_FEE_WAIVED_TYPES: AttendeeType[] = [
  "speaker",
  "invited_guest",
];

/** Rule 8: Speaker and invited guest registrants do not incur main-event guest fees. */
export function shouldWaiveMainGuestFees(reg: {
  attendeeType?: string;
}): boolean {
  return MAIN_EVENT_GUEST_FEE_WAIVED_TYPES.includes(
    reg.attendeeType as AttendeeType,
  );
}

/** Rules 2 & 4: waive member slot for no-charge types, prepaid, or noCharge/prepaid method. */
export function shouldWaiveMemberEventFee(reg: {
  attendeeType?: string;
  prepaid?: boolean;
  paymentMethod?: string;
}): boolean {
  if (reg.prepaid === true) {
    return true;
  }
  if (reg.paymentMethod === "prepaid") {
    return true;
  }
  if (reg.paymentMethod === "noCharge" && isNoChargeMemberType(reg.attendeeType)) {
    return true;
  }
  return isNoChargeMemberType(reg.attendeeType);
}

export function buildStaffRegistrationDraft(
  registration: IRegistration,
  formState: StaffFormPaymentState = {},
): IRegistration {
  const additionalFee = parseAdditionalFee(
    formState.additionalFeeInput ?? registration.additionalFee,
  );
  return {
    ...registration,
    guests: formState.guests ?? registration.guests,
    selectedSpecialEventIds:
      formState.selectedSpecialEventIds ?? registration.selectedSpecialEventIds,
    specialEventGuests:
      formState.specialEventGuestsMap ?? registration.specialEventGuests,
    additionalFee,
  };
}

export function staffRegistrationHasChargeableExtras(
  registration: IRegistration,
  event: IEvent | null | undefined,
  formState: StaffFormPaymentState = {},
): boolean {
  return computeStaffPayableAmount(registration, event, formState) > 0;
}

export function resolveEventMemberFee(
  event: IEvent | null | undefined,
  registration: IRegistration,
): number {
  const fee = event?.fee;
  if (!fee) {
    return 0;
  }
  const shouldUseLateFees = shouldUseLateFeesForRegistration(
    event ?? null,
    registration,
  );
  return roundAmount(
    shouldUseLateFees
      ? (fee.lateFee ?? fee.memberFee ?? fee.fee ?? 0)
      : (fee.memberFee ?? fee.fee ?? 0),
  );
}

/**
 * Prepaid always covers the regular event member fee — never the late fee.
 * Late fees may still apply to guests / special events separately.
 */
export function getPrepaidPaymentAmount(
  event: IEvent | null | undefined,
  _registration?: IRegistration,
): number {
  const fee = event?.fee;
  if (!fee) {
    return 0;
  }
  return roundAmount(fee.memberFee ?? fee.fee ?? 0);
}

export function hasPrepaidPaymentInLedger(
  payments?: Array<{ paymentMethod?: string }> | null,
): boolean {
  return (
    Array.isArray(payments) &&
    payments.some((p) => p.paymentMethod === "prepaid")
  );
}

/**
 * The ledger is the source of truth: a prepaid payment marks the registration
 * as prepaid. Returns the same reference when there is nothing to change.
 */
export function applyPrepaidFlagFromLedger<T extends { prepaid?: boolean }>(
  registration: T,
  payments?: Array<{ paymentMethod?: string }> | null,
): T {
  if (registration.prepaid === true) {
    return registration;
  }
  if (!hasPrepaidPaymentInLedger(payments)) {
    return registration;
  }
  return { ...registration, prepaid: true };
}

/** UI: registration is already treated as prepaid (hide prepaid from New Payment). */
export function registrationAlreadyHasPrepaid(
  registration: IRegistration | null | undefined,
  payments?: Array<{ paymentMethod?: string }> | null,
): boolean {
  if (!registration) {
    return false;
  }
  if (hasPrepaidPaymentInLedger(payments)) {
    return true;
  }
  return registration.prepaid === true || registration.paymentMethod === "prepaid";
}

/** Staff UI: mark registration as prepaid (event fee covered by firm slot). */
export function applyStaffPrepaidSelection(): Partial<IRegistration> {
  return { prepaid: true, paymentMethod: "prepaid" };
}

/** Revert a draft prepaid selection back to the default billable method. */
export function clearStaffPrepaidSelection(
  registration: IRegistration,
  event: IEvent | null | undefined,
  formState: StaffFormPaymentState = {},
): Partial<IRegistration> {
  const draftWithoutPrepaid: IRegistration = {
    ...registration,
    prepaid: false,
    paymentMethod: undefined,
  };
  return {
    prepaid: false,
    paymentMethod: getDefaultPaymentMethodForType(
      draftWithoutPrepaid,
      event,
      formState,
    ),
  };
}

/** Lock Registrant Payment Method when prepaid is fixed (edit or auto annual slot). */
export function shouldLockRegistrantPaymentMethod(params: {
  isEditMode: boolean;
  registration: IRegistration | null | undefined;
  payments?: Array<{ paymentMethod?: string }> | null;
  autoPrepaidLocked?: boolean;
}): boolean {
  const { isEditMode, registration, payments, autoPrepaidLocked } = params;
  if (!registration) {
    return false;
  }
  if (isEditMode && registrationAlreadyHasPrepaid(registration, payments)) {
    return true;
  }
  if (!isEditMode && autoPrepaidLocked === true) {
    return true;
  }
  return false;
}

/**
 * Resolves the amount to post for a staff payment.
 * Prepaid falls back to the regular event member fee when no amount is typed.
 */
export function resolveStaffPaymentIncrement(params: {
  paymentMethod: PaymentMethod | "" | undefined;
  typedAmount: string | undefined;
  registration: IRegistration;
  event: IEvent | null | undefined;
}): number | null {
  const { paymentMethod, typedAmount, registration, event } = params;
  const typed = parsePaymentIncrement(typedAmount);
  if (paymentMethod === "prepaid" && typed == null) {
    const fee = getPrepaidPaymentAmount(event, registration);
    return fee > 0 ? fee : null;
  }
  return typed;
}

export type PrepaidPaymentValidation =
  | { ok: true }
  | { ok: false; message: string };

/** One prepaid payment row per registration (office/firm slot is bypassed on staff). */
export function validatePrepaidStaffPayment(params: {
  paymentMethod: PaymentMethod | "" | undefined;
  registration: IRegistration;
  event: IEvent | null | undefined;
  payments?: Array<{ paymentMethod?: string }> | null;
}): PrepaidPaymentValidation {
  const { paymentMethod, registration, event, payments } = params;
  if (paymentMethod !== "prepaid") {
    return { ok: true };
  }

  if (hasPrepaidPaymentInLedger(payments)) {
    return {
      ok: false,
      message:
        "This registration already has a prepaid payment for the event fee. Use Credit Card, Wire Transfer, or Check for additional amounts.",
    };
  }

  const prepaidAmount = getPrepaidPaymentAmount(event, registration);
  if (prepaidAmount <= 0) {
    return {
      ok: false,
      message: "This event has no member fee to cover with prepaid.",
    };
  }

  return { ok: true };
}

export function isAnnualMeetingEvent(
  event: IEvent | null | undefined,
  registration?: IRegistration,
): boolean {
  const setupType =
    event?.setupType ?? registration?.eventSetupType ?? "";
  return setupType === "annual" || setupType === "Annual Meeting";
}

/**
 * When the server marks a billable registration prepaid (first office slot on an
 * Annual Meeting), staff must still post a prepaid payment row for the event fee.
 */
export function shouldEnsureStaffPrepaidPayment(params: {
  registration: IRegistration;
  event: IEvent | null | undefined;
  payments?: Array<{ paymentMethod?: string }> | null;
}): boolean {
  const { registration, event, payments } = params;
  if (!isAnnualMeetingEvent(event, registration)) {
    return false;
  }
  if (registration.prepaid !== true) {
    return false;
  }
  if (!isBillableMemberType(registration.attendeeType)) {
    return false;
  }
  if (registration.isHostFirm === true) {
    return false;
  }
  if (hasPrepaidPaymentInLedger(payments)) {
    return false;
  }
  return getPrepaidPaymentAmount(event, registration) > 0;
}

/** True when staff save should create the prepaid event-fee payment row. */
export function shouldCreateStaffPrepaidPayment(params: {
  paymentMethod: PaymentMethod | "" | undefined;
  payments?: Array<{ paymentMethod?: string }> | null;
}): boolean {
  const { paymentMethod, payments } = params;
  if (paymentMethod !== "prepaid") {
    return false;
  }
  return !hasPrepaidPaymentInLedger(payments);
}

function isPrepaidRegistration(
  registration: IRegistration,
  formState: StaffFormPaymentState = {},
): boolean {
  return (
    registration.prepaid === true ||
    registration.paymentMethod === "prepaid" ||
    formState.hasPrepaidPaymentInLedger === true
  );
}

/** Member slot fee for staff totals. Prepaid always uses the regular member fee. */
function resolveStaffMemberFee(
  event: IEvent | null | undefined,
  registration: IRegistration,
  formState: StaffFormPaymentState = {},
): number {
  if (isPrepaidRegistration(registration, formState)) {
    return getPrepaidPaymentAmount(event, registration);
  }
  return resolveEventMemberFee(event, registration);
}

function resolveGuestFeePer(
  event: IEvent | null | undefined,
  registration: IRegistration,
): number {
  const fee = event?.fee;
  if (!fee) {
    return 0;
  }
  const shouldUseLateFees = shouldUseLateFeesForRegistration(
    event ?? null,
    registration,
  );
  return shouldUseLateFees
    ? (fee.guestLateFee ?? fee.guestFee ?? 0)
    : (fee.guestFee ?? 0);
}

function sumStaffFeeConcepts(
  registration: IRegistration,
  event: IEvent | null | undefined,
  formState: StaffFormPaymentState,
  options: {
    includeMemberFee: boolean;
    includeGuestFees: boolean;
    includeAdditionalFee?: boolean;
    includeSpecialEvents?: boolean;
  },
): number {
  const draft = buildStaffRegistrationDraft(registration, formState);
  const additionalFee = parseAdditionalFee(draft.additionalFee);
  let amount = 0;

  if (options.includeMemberFee) {
    amount += resolveStaffMemberFee(event, draft, formState);
  }

  if (options.includeGuestFees) {
    const guestCount = normalizeRegistrationGuests(draft).length;
    if (guestCount > 0) {
      amount += resolveGuestFeePer(event, draft) * guestCount;
    }
  }

  if (options.includeAdditionalFee !== false) {
    amount += additionalFee;
  }
  if (options.includeSpecialEvents !== false) {
    amount += getSpecialEventsTotalAmount(event ?? null, draft);
  }
  return roundAmount(amount);
}

/** Registrant registration fees: member or late fee, plus each guest fee. Prepaid waives the member fee. */
export function computeStaffDisplayTotalEventFee(
  registration: IRegistration,
  event: IEvent | null | undefined,
  formState: StaffFormPaymentState = {},
): number {
  return sumStaffFeeConcepts(registration, event, formState, {
    includeMemberFee: !isPrepaidRegistration(registration, formState),
    includeGuestFees: true,
    includeAdditionalFee: false,
    includeSpecialEvents: false,
  });
}

/** Staff payable total (rules 2, 4, 8 applied). Staff never owes fees. */
export function computeStaffPayableAmount(
  registration: IRegistration,
  event: IEvent | null | undefined,
  formState: StaffFormPaymentState = {},
): number {
  const draft = buildStaffRegistrationDraft(registration, formState);
  if (isStaffNoPayAttendee(draft)) {
    return 0;
  }
  return sumStaffFeeConcepts(draft, event, formState, {
    includeMemberFee: !shouldWaiveMemberEventFee(draft),
    includeGuestFees: !shouldWaiveMainGuestFees(draft),
  });
}

/**
 * Total due on Edit Attendee.
 * Prepaid waives the member fee (regular or late). Guest fees still apply.
 * The full amount paid reduces that total; the prepaid slot is not carved out of it.
 */
export function computePayableBalance(
  registration: IRegistration,
  event: IEvent | null | undefined,
  formState: StaffFormPaymentState = {},
): number {
  const draft = buildStaffRegistrationDraft(registration, formState);
  const payableTotal = computeStaffPayableAmount(draft, event, formState);
  const amountPaid = roundAmount(
    typeof draft.amountPaid === "number" && !Number.isNaN(draft.amountPaid)
      ? draft.amountPaid
      : 0,
  );
  return roundAmount(payableTotal - amountPaid);
}

export function getAllowedPaymentMethods(
  registration: IRegistration,
  event: IEvent | null | undefined,
  formState: StaffFormPaymentState = {},
): PaymentMethod[] {
  const type = registration.attendeeType;

  if (isBillableMemberType(type)) {
    const methods: PaymentMethod[] = [...BILLABLE_PAYMENT_METHODS];
    if (getPrepaidPaymentAmount(event, registration) > 0) {
      methods.push("prepaid");
    }
    return methods;
  }

  if (isNoChargeMemberType(type)) {
    const hasExtras = staffRegistrationHasChargeableExtras(
      registration,
      event,
      formState,
    );
    if (hasExtras) {
      return [...BILLABLE_PAYMENT_METHODS];
    }
    return ["noCharge"];
  }

  return [...BILLABLE_PAYMENT_METHODS, "noCharge"];
}

export function getNewPaymentMethodOptions(
  registration: IRegistration,
  event: IEvent | null | undefined,
  formState: StaffFormPaymentState = {},
  options?: { hasPrepaidPayment?: boolean },
): PaymentMethod[] {
  const hasPrepaid =
    options?.hasPrepaidPayment ??
    registrationAlreadyHasPrepaid(registration);

  return getAllowedPaymentMethods(registration, event, formState).filter(
    (method) => {
      if (!NEW_PAYMENT_METHODS.includes(method)) {
        return false;
      }
      // Prepaid may be used only once to cover the event member fee.
      if (method === "prepaid" && hasPrepaid) {
        return false;
      }
      return true;
    },
  );
}

export function getAllowedPaymentStatuses(
  registration: IRegistration,
  event: IEvent | null | undefined,
  formState: StaffFormPaymentState = {},
): StatusType[] {
  const type = registration.attendeeType;
  const balance = computePayableBalance(registration, event, formState);

  if (isBillableMemberType(type)) {
    return ["pending", "completed", "partial"];
  }

  if (type === "sponsor") {
    if (balance > 0) {
      return ["pending", "partial", "paidSPR"];
    }
    return ["paidSPR", "completed", "partial"];
  }

  if (type === "speaker") {
    if (balance > 0) {
      return ["pending", "partial", "paidSPK"];
    }
    return ["paidSPK", "completed", "partial"];
  }

  if (type === "invited_guest" || type === "staff") {
    if (balance > 0) {
      return ["pending", "partial", "completed"];
    }
    return ["completed", "partial"];
  }

  return ["pending", "completed", "partial"];
}

export function registrationHasStaffPayment(
  registration: IRegistration,
  formState: StaffFormPaymentState = {},
): boolean {
  const amountPaid = roundAmount(
    typeof registration.amountPaid === "number" &&
      !Number.isNaN(registration.amountPaid)
      ? registration.amountPaid
      : 0,
  );
  if (amountPaid > 0) {
    return true;
  }
  if (formState.hasPrepaidPaymentInLedger) {
    return true;
  }
  return false;
}

export function derivePaymentStatus(
  registration: IRegistration,
  event: IEvent | null | undefined,
  formState: StaffFormPaymentState = {},
): StatusType {
  const draft = buildStaffRegistrationDraft(registration, formState);
  const type = draft.attendeeType;
  const balance = computePayableBalance(draft, event, formState);
  const hasPayment = registrationHasStaffPayment(draft, formState);
  const hasExtras = staffRegistrationHasChargeableExtras(
    draft,
    event,
    formState,
  );

  if (isNoChargeMemberType(type) && !hasExtras) {
    return getDefaultNoChargeStatus(type);
  }

  if (balance <= 0) {
    if (type === "sponsor") {
      return "paidSPR";
    }
    if (type === "speaker") {
      return "paidSPK";
    }
    return "completed";
  }

  if (hasPayment) {
    return "partial";
  }

  return "pending";
}

export function getDefaultPaymentMethodForType(
  registration: IRegistration,
  event: IEvent | null | undefined,
  formState: StaffFormPaymentState = {},
): PaymentMethod {
  const type = registration.attendeeType;
  const hasExtras = staffRegistrationHasChargeableExtras(
    registration,
    event,
    formState,
  );

  if (isBillableMemberType(type)) {
    if (registration.prepaid === true) {
      return "prepaid";
    }
    return "creditCard";
  }

  if (isNoChargeMemberType(type)) {
    if (hasExtras) {
      return "creditCard";
    }
    return "noCharge";
  }

  return "creditCard";
}

export function applyStaffParticipantPaymentStatus(
  registration: IRegistration,
  event: IEvent | null | undefined,
  formState: StaffFormPaymentState = {},
): void {
  const draft = buildStaffRegistrationDraft(registration, formState);
  registration.paymentStatus = derivePaymentStatus(draft, event, formState);
  registration.totalAmount = computeStaffPayableAmount(draft, event, formState);
}

export function isPaymentMethodAllowed(
  registration: IRegistration,
  event: IEvent | null | undefined,
  method: PaymentMethod | undefined,
  formState: StaffFormPaymentState = {},
): boolean {
  if (!method) {
    return false;
  }
  return getAllowedPaymentMethods(registration, event, formState).includes(method);
}

export function isPaymentStatusAllowed(
  registration: IRegistration,
  event: IEvent | null | undefined,
  status: StatusType | undefined,
  formState: StaffFormPaymentState = {},
): boolean {
  if (!status) {
    return false;
  }
  return getAllowedPaymentStatuses(registration, event, formState).includes(status);
}

export function toPaymentMethodOptions(methods: PaymentMethod[]) {
  return methods.map((key) => ({
    id: key,
    name: paymentMethodEnum[key],
  }));
}

export function toPaymentStatusOptions(statuses: StatusType[]) {
  return statuses.map((key) => ({
    id: key,
    name: statusEnum[key],
  }));
}

export function normalizeStaffPaymentFields(
  registration: IRegistration,
  event: IEvent | null | undefined,
  formState: StaffFormPaymentState = {},
): Partial<IRegistration> {
  const allowedMethods = getAllowedPaymentMethods(registration, event, formState);
  const allowedStatuses = getAllowedPaymentStatuses(registration, event, formState);

  let paymentMethod = registration.paymentMethod;
  if (!paymentMethod || !allowedMethods.includes(paymentMethod)) {
    paymentMethod = getDefaultPaymentMethodForType(registration, event, formState);
  }

  const draft = {
    ...registration,
    paymentMethod,
  };

  let paymentStatus = derivePaymentStatus(draft, event, formState);
  if (!allowedStatuses.includes(paymentStatus)) {
    paymentStatus =
      isNoChargeMemberType(registration.attendeeType) &&
      !staffRegistrationHasChargeableExtras(registration, event, formState)
        ? getDefaultNoChargeStatus(registration.attendeeType)
        : paymentStatus;
  }

  return { paymentMethod, paymentStatus };
}
